/**
 * Usage / metering view (Platform Admin) — the metered numbers that feed per-instance invoice lines.
 *
 * Shows the current billing month's usage for a customer, grouped per Instance and meter
 * (Workflow executions, Tasks, Governed executions, …). "Run rollup now" aggregates the latest
 * raw events into the usage store on demand (the scheduled rollup does this automatically).
 *
 * Read-only inspection — no thresholds or edits here; limits live in the quotas card and pricing in
 * the rate card. Instance names are resolved best-effort; rows fall back to a short id if unresolved.
 */
import React, { useEffect, useState } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const unwrap = (res) => (res && res.data ? res.data : res);

// Curated labels for meters whose auto-humanized form reads poorly.
const METER_LABELS = {
  LLM_INPUT_TOKENS: 'LLM input tokens',
  LLM_OUTPUT_TOKENS: 'LLM output tokens',
  LLM_COST_USD: 'LLM cost (billable)',
  LLM_COST_BYOK_USD: 'LLM cost (BYOK — not billed)',
};
// Monetary meters store micro-USD (USD × 1e6) in the integer value.
const MONETARY_METERS = new Set(['LLM_COST_USD', 'LLM_COST_BYOK_USD']);
// Humanize a stored meter key, e.g. WORKFLOW_EXECUTIONS → "Workflow executions".
const meterLabel = (k) => {
  if (!k) return 'Usage';
  if (METER_LABELS[k]) return METER_LABELS[k];
  const s = String(k).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString());
// Monetary meters store micro-USD (USD × 1e6) in the integer value → render as dollars.
const meterValue = (r) => (MONETARY_METERS.has(r.meterKey)
  ? `$${(Number(r.value || 0) / 1e6).toFixed(2)}`
  : fmt(r.value));
const meterUnit = (r) => (MONETARY_METERS.has(r.meterKey) ? 'USD' : (r.unit || ''));

// Billing window for a cost line: from the period start to today (for the in-progress period),
// or to the period's last day once it has closed. Avoids implying a future month-end is billed.
const todayIso = () => new Date().toISOString().slice(0, 10);
const dayBefore = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const billingWindow = (r) => {
  if (!r.periodStart) return null;
  const start = String(r.periodStart).slice(0, 10);
  const today = todayIso();
  const periodLastDay = r.periodEnd ? dayBefore(String(r.periodEnd).slice(0, 10)) : today;
  const end = today < periodLastDay ? today : periodLastDay; // in-progress → today; closed → last day
  return `Billing ${start} → ${end}`;
};

export function UsageCard({ customerId, budgetUsd = null, selfServe = false }) {
  const { addToast } = useToast();
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});   // instanceId → name
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rolling, setRolling] = useState(false);

  const load = async () => {
    // Self-serve reads the caller's own usage (no customerId); PA reads a chosen customer's.
    if (!selfServe && !customerId) return;
    setLoading(true);
    try {
      const usage = unwrap(await (selfServe ? api.getMyUsage() : api.getCustomerUsage(customerId))) || [];
      const list = Array.isArray(usage) ? usage : [];
      setRows(list);
      setPeriod(list.length ? list[0].periodStart : null);
      // Resolve instance names best-effort (PA sees all instances; harmless if it 403s).
      try {
        const inst = unwrap(await api.getInstances()) || [];
        const map = {};
        (Array.isArray(inst) ? inst : []).forEach((i) => { map[i.id] = i.name; });
        setNames(map);
      } catch (_) { /* fall back to short ids */ }
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Failed to load usage' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId, selfServe]);

  const runRollup = async () => {
    setRolling(true);
    try {
      const res = unwrap(await api.runUsageRollup());
      addToast({ tone: 'success', message: `Rollup complete — ${res?.rolledUp ?? 0} row(s) updated` });
      await load();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Rollup failed' });
    } finally {
      setRolling(false);
    }
  };

  // Group rows by instance, preserving a stable meter order within each.
  const byInstance = {};
  rows.forEach((r) => {
    (byInstance[r.instanceId] = byInstance[r.instanceId] || []).push(r);
  });
  const instanceIds = Object.keys(byInstance).sort((a, b) =>
    (names[a] || a).localeCompare(names[b] || b));

  const instanceName = (id) => names[id] || `Instance ${String(id).slice(0, 8)}…`;

  // Managed LLM budget (soft-warn only, never blocks). Spend = billable LLM cost this month
  // (micro-USD → USD); BYOK is excluded upstream so it never counts against the budget.
  const budget = Number(budgetUsd) > 0 ? Number(budgetUsd) : null;
  const billableMicros = rows
    .filter((r) => r.meterKey === 'LLM_COST_USD')
    .reduce((sum, r) => sum + Number(r.value || 0), 0);
  const spendUsd = billableMicros / 1e6;
  const budgetPct = budget ? Math.min(100, (spendUsd / budget) * 100) : 0;
  const budgetTone = budgetPct >= 100 ? 'danger' : (budgetPct >= 80 ? 'warning' : 'ok');
  const barColor = { danger: 'bg-v2-danger', warning: 'bg-v2-warning', ok: 'bg-v2-action' }[budgetTone];

  return (
    <Card padding="sm">
      <Card.Body>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Activity size={13} className="text-v2-action-strong" />
            <div className="text-[12px] font-semibold text-v2-ink">Usage — current month</div>
          </div>
          <div className="flex items-center gap-2">
            {period && <Pill tone="muted" size="sm">Period: {String(period).slice(0, 7)}</Pill>}
            <Button variant="secondary" size="sm" onClick={load} disabled={loading}
              iconLeft={<RefreshCw size={12} />}>Refresh</Button>
          </div>
        </div>
        <div className="text-[11px] text-v2-ink-muted mb-2">
          Metered consumption per instance — the basis for usage invoice lines. Run a rollup to
          aggregate the latest events into this month’s totals.
        </div>

        {budget && (
          <div className="mb-3 border border-v2-border-subtle rounded-v2-sm px-3 py-2">
            <div className="flex items-center justify-between text-[12px] mb-1">
              <span className="text-v2-ink font-semibold">Managed LLM budget</span>
              <span className="text-v2-ink-muted tabular-nums">
                ${spendUsd.toFixed(2)} of ${Number(budget).toLocaleString()}
              </span>
            </div>
            <div className="h-2 w-full bg-v2-muted rounded-full overflow-hidden">
              <div className={`h-full ${barColor}`} style={{ width: `${budgetPct.toFixed(0)}%` }} />
            </div>
            <div className="text-[10.5px] text-v2-ink-muted mt-1">
              {budgetTone === 'danger'
                ? `Reached your monthly budget — this is a soft limit, so work continues.`
                : `Soft limit — warns as you approach it, never blocks. Bring-your-own-key spend isn’t counted.`}
            </div>
          </div>
        )}

        {loading && <div className="text-v2-ink-muted text-[12px]">Loading usage…</div>}

        {!loading && instanceIds.length === 0 && (
          <div className="text-[12px] text-v2-ink-muted border border-v2-border-subtle rounded-v2-sm px-3 py-3">
            No usage recorded for this month yet. Click “Run rollup now” to aggregate current events.
          </div>
        )}

        {!loading && instanceIds.map((id) => (
          <div key={id} className="mb-3">
            <div className="text-[11px] font-semibold text-v2-ink mb-1">{instanceName(id)}</div>
            <table className="w-full text-[12px] table-fixed">
              <thead>
                <tr className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                  <th className="text-left font-semibold py-1 w-[55%]">Meter</th>
                  <th className="text-right font-semibold py-1 w-[30%] pr-1">Volume</th>
                  <th className="text-left font-semibold py-1 pl-2 w-[15%]">Unit</th>
                </tr>
              </thead>
              <tbody>
                {byInstance[id]
                  .slice()
                  .sort((a, b) => String(a.meterKey).localeCompare(String(b.meterKey)))
                  .map((r) => (
                    <tr key={r.meterKey} className="border-t border-v2-border-subtle align-top">
                      <td className="py-1.5 text-v2-ink">
                        <div>{meterLabel(r.meterKey)}</div>
                        {MONETARY_METERS.has(r.meterKey) && billingWindow(r) && (
                          <div className="text-[10px] text-v2-ink-muted mt-0.5">{billingWindow(r)}</div>
                        )}
                      </td>
                      <td className="py-1.5 pr-1 text-right text-v2-ink tabular-nums">{meterValue(r)}</td>
                      <td className="py-1.5 pl-2 text-v2-ink-muted">{meterUnit(r)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Rollup is a platform-wide admin action — hidden in the self-serve view. */}
        {!selfServe && (
          <div className="flex items-center gap-2 mt-1">
            <Button variant="secondary" size="sm" onClick={runRollup} disabled={rolling}>
              {rolling ? 'Rolling up…' : 'Run rollup now'}
            </Button>
            <span className="text-[11px] text-v2-ink-muted">Aggregates this month’s events platform-wide.</span>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default UsageCard;
